import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Button, List,
  ListItem, ListItemText, Chip, Alert, IconButton, Dialog,
  DialogContent, TextField, MenuItem, Select, FormControl, InputLabel,
  useTheme, useMediaQuery, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as DocumentIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

export default function AssetDetail() {
  const { id } = useParams();
  const [asset, setAsset] = useState(null);
  const [qrCode, setQRCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [renameDialog, setRenameDialog] = useState({ open: false, docId: null, currentName: '' });
  const [newFileName, setNewFileName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [viewDialog, setViewDialog] = useState({ open: false, doc: null, url: '' });
  const [openWorkOrderDialog, setOpenWorkOrderDialog] = useState(false);
  const [workOrderForm, setWorkOrderForm] = useState({ title: '', description: '', priority: 'medium' });
  const [workOrderError, setWorkOrderError] = useState('');
  const [woAttachments, setWoAttachments] = useState([]); // [{file, preview}]
  const [openPMDialog, setOpenPMDialog] = useState(false);
  const [pmForm, setPMForm] = useState({ title: '', schedule_type: 'time_based', interval_value: '', interval_unit: 'days' });
  const [pmError, setPMError] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editError, setEditError] = useState('');
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const { hasRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    priorityColors,
    statusColors,
    borderColor,
    pmTypeBackground,
    pmTypeText,
    tableRowHover,
    accentBlue,
    gradientPrimary,
    gradientPrimaryHover,
  } = useThemeColors();

  const loadAsset = useCallback(async () => {
    try {
      const response = await api.get(`/assets/${id}`);
      setAsset(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadQRCode = useCallback(async () => {
    try {
      const response = await api.get(`/assets/${id}/qrcode`);
      setQRCode(response.data.qrCode);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  const loadDocuments = useCallback(async () => {
    try {
      const response = await api.get(`/assets/${id}/documents`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  }, [id]);

  const loadLocations = useCallback(async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get('/assets');
      const uniqueCats = [...new Set(response.data.map(a => a.category).filter(Boolean))].sort();
      setCategories(uniqueCats);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  useEffect(() => {
    loadAsset();
    loadQRCode();
    loadDocuments();
    loadLocations();
    loadCategories();
  }, [loadAsset, loadQRCode, loadDocuments, loadLocations, loadCategories]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const filesWithNames = files.map(file => {
        const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        return {
          file: file,
          customName: nameWithoutExt,
          originalName: file.name
        };
      });
      setSelectedFiles(filesWithNames);
      setUploadError('');
    }
  };

  const handleFileNameChange = (index, newName) => {
    setSelectedFiles(prev => prev.map((item, i) =>
      i === index ? { ...item, customName: newName } : item
    ));
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setUploadError('Please select at least one file');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      for (const item of selectedFiles) {
        const extension = item.originalName.substring(item.originalName.lastIndexOf('.'));
        const newFileName = item.customName + extension;
        const renamedFile = new File([item.file], newFileName, { type: item.file.type });

        const formData = new FormData();
        formData.append('document', renamedFile);

        await api.post(`/assets/${id}/documents`, formData);
      }

      setOpenUploadDialog(false);
      setSelectedFiles([]);
      loadDocuments();
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload document(s)';
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    try {
      await api.delete(`/assets/${id}/documents/${docId}`);
      loadDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  const handleOpenRenameDialog = (doc) => {
    const nameWithoutExt = doc.original_filename.substring(0, doc.original_filename.lastIndexOf('.')) || doc.original_filename;
    setNewFileName(nameWithoutExt);
    setRenameDialog({ open: true, docId: doc.id, currentName: doc.original_filename });
    setRenameError('');
  };

  const handleCloseRenameDialog = () => {
    setRenameDialog({ open: false, docId: null, currentName: '' });
    setNewFileName('');
    setRenameError('');
  };

  const handleRenameDocument = async () => {
    if (!newFileName.trim()) {
      setRenameError('Filename cannot be empty');
      return;
    }

    try {
      await api.patch(`/assets/${id}/documents/${renameDialog.docId}`, {
        new_name: newFileName.trim()
      });
      handleCloseRenameDialog();
      loadDocuments();
    } catch (err) {
      setRenameError(err.response?.data?.error || 'Failed to rename document');
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await api.get(`/assets/${id}/documents/${docId}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download document');
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      const response = await api.get(`/assets/${id}/documents/${doc.id}/view`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: doc.file_type });
      const fileUrl = window.URL.createObjectURL(blob);
      setViewDialog({ open: true, doc: doc, url: fileUrl });
    } catch (err) {
      console.error('View failed:', err);
      alert('Failed to load document for viewing');
    }
  };

  const handleCloseViewDialog = () => {
    if (viewDialog.url) {
      window.URL.revokeObjectURL(viewDialog.url);
    }
    setViewDialog({ open: false, doc: null, url: '' });
  };

  const isImageFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  };

  const isPdfFile = (filename) => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  const handleCreateWorkOrder = async () => {
    if (!workOrderForm.title) {
      setWorkOrderError('Title is required');
      return;
    }

    try {
      const response = await api.post('/work-orders', {
        asset_id: asset.id,
        title: workOrderForm.title,
        description: workOrderForm.description,
        priority: workOrderForm.priority,
      });

      // Upload attachments
      if (woAttachments.length > 0 && response.data.id) {
        for (const item of woAttachments) {
          const fd = new FormData();
          fd.append('file', item.file);
          await api.post(`/uploads/work-order/${response.data.id}`, fd);
        }
      }

      setOpenWorkOrderDialog(false);
      setWorkOrderForm({ title: '', description: '', priority: 'medium' });
      woAttachments.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview); });
      setWoAttachments([]);
      setWorkOrderError('');
      loadAsset();
    } catch (err) {
      setWorkOrderError(err.response?.data?.error || 'Failed to create work order');
    }
  };

  const handleCreatePM = async () => {
    if (!pmForm.title || !pmForm.interval_value) {
      setPMError('Title and Interval are required');
      return;
    }

    try {
      await api.post('/pm-schedules', {
        asset_id: asset.id,
        title: pmForm.title,
        schedule_type: pmForm.schedule_type,
        interval_value: parseInt(pmForm.interval_value),
        interval_unit: pmForm.interval_unit,
      });
      setOpenPMDialog(false);
      setPMForm({ title: '', schedule_type: 'time_based', interval_value: '', interval_unit: 'days' });
      setPMError('');
      loadAsset();
    } catch (err) {
      setPMError(err.response?.data?.error || 'Failed to create PM schedule');
    }
  };

  const handleOpenEdit = () => {
    setEditFormData({
      asset_number: asset.asset_number || '',
      asset_description: asset.asset_description || '',
      category: asset.category || '',
      location: asset.location || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      serial_number: asset.serial_number || '',
      department: asset.department || '',
    });
    setEditError('');
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.asset_number || !editFormData.asset_description) {
      setEditError('Asset Number and Description are required');
      return;
    }
    try {
      await api.put(`/assets/${id}`, editFormData);
      setOpenEditDialog(false);
      setEditError('');
      loadAsset();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update asset');
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    if (status === 'in_progress') return 'In Progress';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Status chip component
  const StatusChip = ({ status }) => {
    const colors = statusColors[status] || statusColors.assigned;
    return (
      <Chip
        label={formatStatus(status)}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  // Priority chip component
  const PriorityChip = ({ priority }) => {
    const colors = priorityColors[priority] || priorityColors.medium;
    return (
      <Chip
        label={priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'N/A'}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  if (loading || !asset) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: accentBlue, fontWeight: 600 }}>
          {asset.asset_number} - {asset.asset_description}
        </Typography>
        {hasRole('administrator', 'supervisor') && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenEdit}
            sx={{
              borderColor: navyBlue,
              color: accentBlue,
              borderRadius: 2,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: navyBlue,
                backgroundColor: 'rgba(30, 58, 95, 0.08)',
              },
            }}
          >
            {isMobile ? 'Edit' : 'Edit Asset'}
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Typography variant="h6" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>Asset Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography><strong>Category:</strong>{' '}
                  {asset.category ? (
                    <Chip
                      label={asset.category}
                      size="small"
                      sx={{
                        backgroundColor: pmTypeBackground,
                        color: pmTypeText,
                        border: '1px solid #2196f3',
                        fontWeight: 500,
                      }}
                    />
                  ) : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Location:</strong> {asset.location || '-'}</Typography>
              </Grid>
              <Grid item xs={6}><Typography><strong>Manufacturer:</strong> {asset.manufacturer || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography><strong>Model:</strong> {asset.model || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography><strong>Serial:</strong> {asset.serial_number || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography><strong>Department:</strong> {asset.department || '-'}</Typography></Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: accentBlue, fontWeight: 600 }}>Work Orders</Typography>
              {hasRole('technician', 'supervisor', 'administrator') && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenWorkOrderDialog(true)}
                    sx={{
                      background: gradientPrimary,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        background: gradientPrimaryHover,
                      },
                    }}
                  >
                    {isMobile ? 'New' : 'Create Work Order'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenPMDialog(true)}
                    sx={{
                      borderColor: navyBlue,
                      color: accentBlue,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {isMobile ? 'PM' : 'Create PM'}
                  </Button>
                </Box>
              )}
            </Box>
            {asset.workOrders?.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No work orders</Typography>
            ) : (
              <List>
                {asset.workOrders?.slice(0, 5).map((wo) => (
                  <ListItem
                    key={wo.id}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      border: `1px solid ${borderColor}`,
                      '&:hover': { backgroundColor: '#f8f9fa' }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, color: accentBlue }}>
                          {wo.work_order_number} - {wo.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <StatusChip status={wo.status} />
                          {wo.priority && <PriorityChip priority={wo.priority} />}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: accentBlue, fontWeight: 600 }}>Documents</Typography>
              {hasRole('technician', 'supervisor', 'administrator') && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => setOpenUploadDialog(true)}
                  sx={{
                    background: gradientPrimary,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      background: gradientPrimaryHover,
                    },
                  }}
                >
                  Upload
                </Button>
              )}
            </Box>
            {documents.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No documents uploaded
              </Typography>
            ) : (
              <List>
                {documents.map((doc) => (
                  <ListItem
                    key={doc.id}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      border: `1px solid ${borderColor}`,
                      '&:hover': { backgroundColor: '#f8f9fa' }
                    }}
                    secondaryAction={
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDocument(doc)}
                          title="View"
                          sx={{ color: accentBlue }}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                          title="Download"
                          sx={{ color: accentBlue }}
                        >
                          <DownloadIcon />
                        </IconButton>
                        {hasRole('technician', 'supervisor', 'administrator') && (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenRenameDialog(doc)}
                            title="Rename"
                            sx={{ color: accentBlue }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {hasRole('supervisor', 'administrator') && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteDocument(doc.id)}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    }
                  >
                    <DocumentIcon sx={{ mr: 2, color: accentBlue }} />
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 500 }}>{doc.original_filename}</Typography>}
                      secondary={`Uploaded ${new Date(doc.uploaded_at).toLocaleDateString()}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: `1px solid ${borderColor}` }}>
            <Typography variant="h6" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>QR Code</Typography>
            {qrCode && <img src={qrCode} alt="QR Code" style={{ maxWidth: '100%' }} />}
            <Typography variant="body2" display="block" sx={{ mt: 2, color: accentBlue, fontWeight: 500 }}>
              Scan to create maintenance request
            </Typography>
            <Typography variant="body2" display="block" sx={{ color: accentBlue, fontWeight: 500 }}>
              Escanear para crear solicitud de mantenimiento
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Upload Document Dialog */}
      <Dialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Upload Document
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
                sx={{ borderColor: navyBlue, color: accentBlue, borderRadius: 2, textTransform: 'none', py: 1.5 }}
              >
                Select Files (Multiple)
                <input
                  type="file"
                  hidden
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  multiple
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                You can select multiple photos or documents at once
              </Typography>
            </Grid>
            {selectedFiles.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Edit the file names below to describe what each photo/document is
                </Typography>
                {selectedFiles.map((item, index) => {
                  const extension = item.originalName.substring(item.originalName.lastIndexOf('.'));
                  return (
                    <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label={`File ${index + 1} Name`}
                          value={item.customName}
                          onChange={(e) => handleFileNameChange(index, e.target.value)}
                          helperText={`Will be saved as: ${item.customName}${extension} (${(item.file.size / 1024).toFixed(1)} KB)`}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveFile(index)}
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  );
                })}
              </Grid>
            )}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => {
                setOpenUploadDialog(false);
                setSelectedFiles([]);
                setUploadError('');
              }}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={selectedFiles.length === 0 || uploading}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Rename Document Dialog */}
      <Dialog open={renameDialog.open} onClose={handleCloseRenameDialog} maxWidth="sm" fullWidth>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Rename Document
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {renameError && <Alert severity="error" sx={{ mb: 2 }}>{renameError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current name: <strong>{renameDialog.currentName}</strong>
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="New File Name"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRenameDocument();
              }
            }}
            helperText={`Will be saved as: ${newFileName}${renameDialog.currentName ? renameDialog.currentName.substring(renameDialog.currentName.lastIndexOf('.')) : ''}`}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button onClick={handleCloseRenameDialog} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
              Cancel
            </Button>
            <Button
              onClick={handleRenameDocument}
              variant="contained"
              disabled={!newFileName.trim()}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Rename
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={handleCloseViewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', borderRadius: 2 }
        }}
      >
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {viewDialog.doc?.original_filename}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleDownloadDocument(viewDialog.doc?.id, viewDialog.doc?.original_filename)}
              title="Download"
              sx={{ color: 'white' }}
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {viewDialog.doc && isImageFile(viewDialog.doc.original_filename) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor: '#000',
                p: 2
              }}
            >
              <img
                src={viewDialog.url}
                alt={viewDialog.doc.original_filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          {viewDialog.doc && isPdfFile(viewDialog.doc.original_filename) && (
            <iframe
              src={viewDialog.url}
              title={viewDialog.doc.original_filename}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
            />
          )}
          {viewDialog.doc && !isImageFile(viewDialog.doc.original_filename) && !isPdfFile(viewDialog.doc.original_filename) && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <DocumentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Preview not available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This file type cannot be previewed in the browser.
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadDocument(viewDialog.doc?.id, viewDialog.doc?.original_filename)}
                sx={{
                  background: gradientPrimary,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Download to View
              </Button>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Button onClick={handleCloseViewDialog} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Create Work Order Dialog */}
      <Dialog open={openWorkOrderDialog} onClose={() => setOpenWorkOrderDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Create Work Order for {asset.asset_number}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {workOrderError && <Alert severity="error" sx={{ mb: 2 }}>{workOrderError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                required
                value={workOrderForm.title}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={workOrderForm.description}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                Priority
              </Typography>
              <ToggleButtonGroup
                value={workOrderForm.priority}
                exclusive
                onChange={(e, value) => value && setWorkOrderForm({ ...workOrderForm, priority: value })}
                fullWidth
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'emergency', label: 'Emergency' },
                ].map((priority) => (
                  <ToggleButton
                    key={priority.value}
                    value={priority.value}
                    sx={{
                      flex: 1,
                      py: 1,
                      borderRadius: '8px !important',
                      border: `1px solid ${priorityColors[priority.value].border} !important`,
                      backgroundColor: workOrderForm.priority === priority.value ? priorityColors[priority.value].bg : 'white',
                      color: priorityColors[priority.value].text,
                      '&.Mui-selected': {
                        backgroundColor: priorityColors[priority.value].bg,
                        color: priorityColors[priority.value].text,
                        fontWeight: 600,
                      },
                      '&:hover': {
                        backgroundColor: priorityColors[priority.value].bg,
                      },
                      textTransform: 'none',
                    }}
                  >
                    {priority.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                Attachments (optional)
              </Typography>
              <Box
                component="label"
                sx={{
                  border: `2px dashed ${borderColor}`,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': { borderColor: accentBlue, bgcolor: 'action.hover' }
                }}
              >
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const newItems = files.map(file => ({
                      file,
                      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
                    }));
                    setWoAttachments(prev => [...prev, ...newItems]);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <AttachFileIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Tap to attach photos or PDFs
                </Typography>
              </Box>
              {woAttachments.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {woAttachments.map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
                      {item.preview ? (
                        <Box component="img" src={item.preview} alt="Preview" sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} />
                      ) : (
                        <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.selected', borderRadius: 1 }}>
                          {item.file.type === 'application/pdf'
                            ? <PdfIcon sx={{ fontSize: 22, color: '#ef5350' }} />
                            : <ImageIcon sx={{ fontSize: 22, color: 'text.secondary' }} />}
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>{item.file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{(item.file.size / 1024 / 1024).toFixed(2)} MB</Typography>
                      </Box>
                      <IconButton size="small" onClick={() => {
                        if (item.preview) URL.revokeObjectURL(item.preview);
                        setWoAttachments(prev => prev.filter((_, i) => i !== idx));
                      }} sx={{ color: '#f44336' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => {
                setOpenWorkOrderDialog(false);
                setWorkOrderForm({ title: '', description: '', priority: 'medium' });
                woAttachments.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview); });
                setWoAttachments([]);
                setWorkOrderError('');
              }}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateWorkOrder}
              variant="contained"
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Create
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Create PM Schedule Dialog */}
      <Dialog open={openPMDialog} onClose={() => { setOpenPMDialog(false); setPMError(''); }} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Create PM Schedule for {asset.asset_number}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {pmError && <Alert severity="error" sx={{ mb: 2 }}>{pmError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                required
                value={pmForm.title}
                onChange={(e) => setPMForm({ ...pmForm, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                Schedule Type
              </Typography>
              <ToggleButtonGroup
                value={pmForm.schedule_type}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    setPMForm({
                      ...pmForm,
                      schedule_type: value,
                      interval_unit: value === 'time_based' ? 'days' : 'hours'
                    });
                  }
                }}
                fullWidth
              >
                <ToggleButton
                  value="time_based"
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: '8px 0 0 8px !important',
                    border: `1px solid ${navyBlue} !important`,
                    backgroundColor: pmForm.schedule_type === 'time_based' ? pmTypeBackground : 'inherit',
                    color: accentBlue,
                    '&.Mui-selected': { backgroundColor: pmTypeBackground, color: accentBlue, fontWeight: 600 },
                    '&:hover': { backgroundColor: tableRowHover },
                    textTransform: 'none',
                  }}
                >
                  Time Based
                </ToggleButton>
                <ToggleButton
                  value="usage_based"
                  sx={{
                    flex: 1,
                    py: 1.5,
                    borderRadius: '0 8px 8px 0 !important',
                    border: `1px solid ${navyBlue} !important`,
                    borderLeft: 'none !important',
                    backgroundColor: pmForm.schedule_type === 'usage_based' ? pmTypeBackground : 'inherit',
                    color: accentBlue,
                    '&.Mui-selected': { backgroundColor: pmTypeBackground, color: accentBlue, fontWeight: 600 },
                    '&:hover': { backgroundColor: tableRowHover },
                    textTransform: 'none',
                  }}
                >
                  Usage Based
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Interval Value"
                type="number"
                required
                value={pmForm.interval_value}
                onChange={(e) => setPMForm({ ...pmForm, interval_value: e.target.value })}
                inputProps={{ min: 1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                Interval Unit
              </Typography>
              <ToggleButtonGroup
                value={pmForm.interval_unit}
                exclusive
                onChange={(e, value) => value && setPMForm({ ...pmForm, interval_unit: value })}
                fullWidth
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {(pmForm.schedule_type === 'time_based'
                  ? [
                      { value: 'days', label: 'Days' },
                      { value: 'weeks', label: 'Weeks' },
                      { value: 'months', label: 'Months' },
                      { value: 'years', label: 'Years' },
                    ]
                  : [
                      { value: 'hours', label: 'Hours' },
                      { value: 'cycles', label: 'Cycles' },
                    ]
                ).map((unit, index, arr) => (
                  <ToggleButton
                    key={unit.value}
                    value={unit.value}
                    sx={{
                      flex: 1,
                      py: 1,
                      borderRadius: index === 0 ? '8px 0 0 8px !important' : index === arr.length - 1 ? '0 8px 8px 0 !important' : '0 !important',
                      border: `1px solid ${navyBlue} !important`,
                      borderLeft: index === 0 ? `1px solid ${navyBlue} !important` : 'none !important',
                      backgroundColor: pmForm.interval_unit === unit.value ? pmTypeBackground : 'inherit',
                      color: accentBlue,
                      '&.Mui-selected': { backgroundColor: pmTypeBackground, color: accentBlue, fontWeight: 600 },
                      '&:hover': { backgroundColor: tableRowHover },
                      textTransform: 'none',
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    {unit.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => {
                setOpenPMDialog(false);
                setPMForm({ title: '', schedule_type: 'time_based', interval_value: '', interval_unit: 'days' });
                setPMError('');
              }}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePM}
              variant="contained"
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Create
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Edit Asset
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Asset Number" required
                value={editFormData.asset_number || ''}
                onChange={(e) => setEditFormData({ ...editFormData, asset_number: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Description" required
                value={editFormData.asset_description || ''}
                onChange={(e) => setEditFormData({ ...editFormData, asset_description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editFormData.category || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  label="Category"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={editFormData.location || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                  label="Location"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.name}>{loc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Manufacturer"
                value={editFormData.manufacturer || ''}
                onChange={(e) => setEditFormData({ ...editFormData, manufacturer: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Model"
                value={editFormData.model || ''}
                onChange={(e) => setEditFormData({ ...editFormData, model: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Serial Number"
                value={editFormData.serial_number || ''}
                onChange={(e) => setEditFormData({ ...editFormData, serial_number: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Department"
                value={editFormData.department || ''}
                onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button
              onClick={() => setOpenEditDialog(false)}
              size={isMobile ? 'large' : 'medium'}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              size={isMobile ? 'large' : 'medium'}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: isMobile ? 4 : 3,
                py: isMobile ? 1.5 : 1,
                fontSize: isMobile ? '1rem' : '0.875rem',
              }}
            >
              Update
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
